import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { testCasesApi } from '@/api/testCases'
import { useTenantKey } from './useTenantKey'
import type { TestCaseFilters, PaginationParams, UpdateTestCaseRequest, TestRunFilters, PushTestCaseRequest, PushTestCasesRequest } from '@/types'

export function useTestCases(projectId: string, workItemId: string, pagination?: PaginationParams, filters?: TestCaseFilters) {
  const tenantKey = useTenantKey()
  const params = pagination || filters ? { ...pagination, ...filters } : undefined

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', workItemId, 'test-cases', params),
    queryFn: () => testCasesApi.getByWorkItem(projectId, workItemId, params),
    enabled: !!projectId && !!workItemId,
    staleTime: 0,
  })
}

export function useTestCasesByTestSuite(
  projectId: string,
  testSuiteId: string,
  pagination?: PaginationParams,
  filters?: TestCaseFilters,
) {
  const tenantKey = useTenantKey()
  const params = pagination || filters ? { ...pagination, ...filters } : undefined

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'test-suites', testSuiteId, 'test-cases', params),
    queryFn: () => testCasesApi.getByTestSuite(projectId, testSuiteId, params),
    enabled: !!projectId && !!testSuiteId,
    staleTime: 0,
  })
}

export function useTestCase(projectId: string, testCaseId: string | null) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'test-cases', testCaseId!),
    queryFn: () => testCasesApi.getById(projectId, testCaseId!),
    enabled: !!projectId && !!testCaseId,
    staleTime: 0,
  })
}

export function useDeleteTestCase(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (testCaseId: string) => testCasesApi.delete(projectId, testCaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-plans') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-suites') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
    },
  })
}

export function useTestCaseAutomationLinks(projectId: string, testCaseId: string | null) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'test-cases', testCaseId!, 'automation-links'),
    queryFn: () => testCasesApi.getAutomationLinks(projectId, testCaseId!),
    enabled: !!projectId && !!testCaseId,
    staleTime: 0,
  })
}

export function useTestRunsByTestCase(
  projectId: string,
  testCaseId: string | null,
  pagination?: PaginationParams,
  filters?: TestRunFilters,
) {
  const tenantKey = useTenantKey()
  const params = pagination || filters ? { ...pagination, ...filters } : undefined

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'test-cases', testCaseId!, 'runs', params),
    queryFn: () => testCasesApi.getRuns(projectId, testCaseId!, params),
    enabled: !!projectId && !!testCaseId,
    staleTime: 0,
  })
}

export function useUpdateTestCase(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTestCaseRequest }) =>
      testCasesApi.update(projectId, id, data),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-plans') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-suites') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-cases', vars.id) })
    },
  })
}

export function usePushTestCaseToDevOps(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ testCaseId, data }: { testCaseId: string; data: PushTestCaseRequest }) =>
      testCasesApi.pushToDevOps(projectId, testCaseId, data),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-cases', vars.testCaseId) })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-suites') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
    },
  })
}

export function usePushTestCasesToDevOps(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (data: PushTestCasesRequest) => testCasesApi.pushManyToDevOps(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-cases') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-suites') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
    },
  })
}
